import type { SpaceIndicatorItem } from '@components/SpaceIndicator';

interface SummaryLike {
  id?: string | null;
  name: string;
  image_url?: string | null;
}

/**
 * Build a combined SpaceIndicatorItem[] from space summaries, event summaries,
 * and an optional single-space fallback. Used by QuestionCard, ProfileCardHeader,
 * and QuestionDetails to drive the SpaceIndicator / SpacePill / SpacesSheet
 * components.
 *
 * Fallback semantics: `fallbackSpace` is used when no space summary actually
 * yields a usable item (i.e. the summaries are absent OR every entry has a
 * falsy `id`). This avoids the bug where a non-empty `spaceSummaries` with all
 * id-less entries would silently produce no indicator at all (Copilot review
 * on PR #505 surfaced this edge case).
 */
export function buildSpaceEventItems(
  spaceSummaries: SummaryLike[] | null | undefined,
  eventSummaries: SummaryLike[] | null | undefined,
  fallbackSpace: { id: string; name: string; image_url?: string | null } | null | undefined,
  authorLiveEvent?: SummaryLike | null,
): SpaceIndicatorItem[] {
  const items: SpaceIndicatorItem[] = [];

  // Push spaces with valid ids; track count to decide whether the fallback applies.
  const spacesBeforePush = items.length;
  for (const s of spaceSummaries ?? []) {
    if (s.id) items.push({ id: s.id, name: s.name, image_url: s.image_url ?? null, type: 'space' });
  }
  // Fallback only when NOTHING was pushed from the summaries (covers both
  // "no summaries provided" and "summaries provided but all id-less").
  if (items.length === spacesBeforePush && fallbackSpace?.id) {
    items.push({
      id: fallbackSpace.id,
      name: fallbackSpace.name,
      image_url: fallbackSpace.image_url ?? null,
      type: 'space',
    });
  }

  // Skip the entry that IS the live event (W2-5 M2): a question posted directly
  // to it carries it in event_summaries too, and without the dedupe the live
  // "at {event}" chip would coexist with a plain duplicate row in the overflow.
  for (const e of eventSummaries ?? []) {
    if (e.id && e.id !== authorLiveEvent?.id) {
      items.push({ id: e.id, name: e.name, image_url: e.image_url ?? null, type: 'event' });
    }
  }

  // Live Layer v1 (L12): while the author is going to a live event of this
  // space, the space feed annotates the question with `author_live_event`.
  // Prepend it as the first item so the SpaceIndicator flips into its live
  // "at {event}" state (the space stays behind as its cross-fade underlay;
  // the "+N" overflow is hidden while live — ruling 2026-07-10).
  // Guarded on `.id` like every other push (keeps `id: string` sound); callers
  // that never pass it — or pass null/undefined — get byte-identical output.
  if (authorLiveEvent?.id) {
    items.unshift({
      id: authorLiveEvent.id,
      name: authorLiveEvent.name,
      image_url: authorLiveEvent.image_url ?? null,
      type: 'event',
      live: true,
    });
  }

  return items;
}

/**
 * The "+N" beside a post's Space: how many more Spaces the post is in. Events never count (product
 * ruling 2026-10-12, TECH-456), so a post in one Space and one Event shows no "+1", though the
 * sheet its indicator opens still lists both.
 *
 * What is counted is every Space other than the face, the first item. A Space face leaves the other
 * Spaces; an Event face (a post in no Space, or the live "at {event}" state) leaves all of them.
 */
export function countOtherSpaces(items: readonly SpaceIndicatorItem[]): number {
  const spaceCount = items.filter(item => item.type === 'space').length;
  const faceIsSpace = items.length > 0 && items[0].type === 'space';
  return faceIsSpace ? spaceCount - 1 : spaceCount;
}
